import React from "react";
import { Link } from "react-router-dom";
import { HardHat, Wrench, Factory, Building2, Drill, Settings, ArrowRight, CheckCircle2 } from "lucide-react";
import { SectionLabel } from "../components/SectionHeading";
import { useSite, absoluteUrl } from "../context/SiteContext";
import SmartImage from "../components/SmartImage";

const ICONS = { HardHat, Wrench, Factory, Building2, Drill, Settings };

export default function ServicesPage() {
  const { services } = useSite();
  return (
    <div data-testid="services-page">
      <section className="border-b border-[#1F3550] relative">
        <div className="absolute inset-0 blueprint-grid opacity-30" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-8 py-24 md:py-32">
          <SectionLabel code="S/00">Services</SectionLabel>
          <h1 className="mt-6 font-heading font-black text-4xl md:text-6xl uppercase tracking-tight leading-[0.95] max-w-4xl">
            Heavy industry capability,<br /> <span className="text-[#F39C12]">delivered end-to-end</span>.
          </h1>
          <p className="mt-6 text-lg text-[#94A3B8] max-w-3xl leading-relaxed">
            Six disciplines, one accountable team. We take projects from drawing to running plant — with the engineering rigour heavy industry demands.
          </p>
        </div>
      </section>

      <section>
        {services.map((s, idx) => {
          const Icon = ICONS[s.icon] || Wrench;
          const reverse = idx % 2 === 1;
          return (
            <div key={s.id} data-testid={`service-section-${s.id}`} id={s.id} className={`border-b border-[#1F3550] ${idx % 2 === 0 ? "bg-[#050A0F]" : "bg-[#0D1B2A]"}`}>
              <div className="max-w-7xl mx-auto px-6 lg:px-8 py-20 md:py-28 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
                <div className={`lg:col-span-5 ${reverse ? "lg:order-2" : ""}`}>
                  <div className="border border-[#1F3550] relative overflow-hidden aspect-[4/3]">
                    <SmartImage src={s.image} alt={s.title} imgClassName="grayscale hover:grayscale-0 transition-all duration-700" />
                    <div className="absolute top-4 left-4 font-mono text-[10px] tracking-[0.3em] text-[#F39C12] bg-[#050A0F]/80 px-2 py-1 z-10">{s.code}</div>
                  </div>
                </div>
                <div className={`lg:col-span-7 ${reverse ? "lg:order-1" : ""}`}>
                  <Icon className="h-9 w-9 text-[#F39C12]" />
                  <h2 className="mt-5 font-heading font-black text-3xl md:text-4xl uppercase tracking-tight leading-tight">{s.title}</h2>
                  <p className="mt-5 text-[#94A3B8] text-base md:text-lg leading-relaxed">{s.description}</p>
                  {s.highlights && s.highlights.length > 0 && (
                    <ul className="mt-7 space-y-3">
                      {s.highlights.map((h, i) => (
                        <li key={i} className="flex items-start gap-3 text-[#F8FAFC]">
                          <CheckCircle2 className="h-5 w-5 text-[#F39C12] mt-0.5 flex-shrink-0" />
                          <span className="text-sm">{h}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                  <Link to="/contact" className="mt-8 inline-flex items-center gap-2 border border-[#F39C12] text-[#F39C12] px-6 py-3 font-bold uppercase tracking-widest text-xs hover:bg-[#F39C12]/10 transition-colors">
                    Discuss this service <ArrowRight className="h-4 w-4" />
                  </Link>
                </div>
              </div>
            </div>
          );
        })}
        {services.length === 0 && (
          <div className="max-w-7xl mx-auto px-6 py-32 text-center text-[#94A3B8]">No services configured yet.</div>
        )}
      </section>
    </div>
  );
}
